import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  TrendingDown, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  DollarSign,
  BarChart3,
  FileCheck,
  Target
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  RadialBarChart,
  RadialBar,
  PieChart,
  Pie
} from "recharts";

const MetricCard = ({ label, value, subtext, icon: Icon, color = "slate" }) => {
  const colorClasses = {
    emerald: "text-emerald-600 bg-emerald-50 border-emerald-200",
    amber: "text-amber-600 bg-amber-50 border-amber-200",
    red: "text-red-600 bg-red-50 border-red-200",
    blue: "text-blue-600 bg-blue-50 border-blue-200",
    slate: "text-slate-600 bg-slate-50 border-slate-200",
  };
  
  return (
    <div className={`border rounded-md p-3 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} />
        <span className="text-sm uppercase tracking-wider font-bold">{label}</span>
      </div>
      <div className="text-xl font-bold">{value}</div>
      {subtext && <div className="text-sm opacity-70">{subtext}</div>}
    </div>
  );
};

const RiskBadge = ({ level }) => {
  const styles = {
    low: "bg-emerald-100 text-emerald-700",
    medium: "bg-amber-100 text-amber-700",
    high: "bg-red-100 text-red-700",
  };
  return (
    <Badge className={`text-sm ${styles[level] || styles.medium}`}>
      {level?.toUpperCase()} RISK
    </Badge>
  );
};

const Section = ({ title, icon: Icon, children, color = "slate" }) => {
  const bgColors = {
    slate: "bg-slate-50",
    blue: "bg-blue-50",
    amber: "bg-amber-50",
    emerald: "bg-emerald-50",
  };
  
  return (
    <div className={`rounded-md border border-slate-200 overflow-hidden`}>
      <div className={`${bgColors[color]} px-4 py-2 border-b border-slate-200 flex items-center gap-2`}>
        <Icon size={14} className="text-slate-600" />
        <span className="text-base font-bold uppercase tracking-wider text-slate-700">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-slate-200 rounded-md shadow-lg">
        <p className="text-sm font-bold text-slate-700">{`Year ${label}`}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {`${entry.name}: ${entry.value}%`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Asset Quality Gauge Component
const AssetQualityGauge = ({ score, grade }) => {
  const gaugeData = [
    { name: 'Score', value: score, fill: score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444' }
  ];
  
  return (
    <div className="flex flex-col items-center">
      <ResponsiveContainer width={180} height={120}>
        <RadialBarChart 
          cx="50%" 
          cy="100%" 
          innerRadius="60%" 
          outerRadius="100%" 
          barSize={12} 
          data={gaugeData}
          startAngle={180} 
          endAngle={0}
        >
          <RadialBar
            background={{ fill: '#e2e8f0' }}
            dataKey="value"
            cornerRadius={6}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="text-center -mt-8">
        <div className="text-3xl font-bold text-slate-800">{score}</div>
        <Badge className={`mt-1 ${score >= 80 ? 'bg-emerald-100 text-emerald-700' : score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
          Grade {grade}
        </Badge>
      </div>
    </div>
  );
};

export default function BankAnalysis({ data }) {
  if (!data || data.error) return null;
  
  const {
    executive_summary,
    asset_quality_score,
    asset_quality_grade,
    market_position,
    depreciation_analysis,
    loan_recommendations,
    npa_risk_model,
    value_projections,
    monitoring_schedule,
  } = data;

  const getScoreColor = (score) => {
    if (score >= 80) return "emerald";
    if (score >= 60) return "amber";
    return "red";
  };

  // Prepare depreciation curve data for chart
  const depreciationChartData = depreciation_analysis?.depreciation_curve_data?.map(item => ({
    year: item.year,
    standard: parseFloat(item.standard) || 0,
    adjusted: parseFloat(String(item.adjusted).replace('%', '')) || 0,
  })) || [];

  // Prepare NPA risk factors for bar chart
  const npaRiskFactors = npa_risk_model?.risk_factors_weighted?.map(factor => ({
    name: factor.factor?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase())?.substring(0, 12) || '',
    weight: factor.weight || 0,
    score: parseFloat(factor.score) || 0,
    contribution: parseFloat(String(factor.contribution).replace('%', '')) || 0,
  })) || [];

  // Prepare value projection data
  const valueProjectionData = value_projections?.projection_data?.map(item => ({
    months: item.months,
    withMaintenance: parseFloat(String(item.with_maintenance).replace('%', '')) || 0,
    withoutMaintenance: parseFloat(String(item.without_maintenance).replace('%', '')) || 0,
  })) || [];

  // Colors for charts
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden" data-testid="bank-analysis-card">
      <div className="border-b border-slate-200 p-3 bg-gradient-to-r from-blue-50 to-cyan-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 size={16} className="text-blue-600" />
          <span className="text-base font-bold uppercase tracking-wider text-blue-800">
            Bank / Lender Analysis
          </span>
          <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-sm">
            ASSET FINANCING VIEW
          </Badge>
        </div>
      </div>
      
      <CardContent className="p-4 space-y-4">
        {/* Executive Summary + Asset Quality Gauge */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            {executive_summary && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md h-full">
                <div className="flex items-center gap-2 mb-2">
                  <FileCheck size={14} className="text-blue-600" />
                  <span className="text-base font-bold text-blue-800 uppercase">Executive Summary</span>
                </div>
                <p className="text-sm text-slate-700">{executive_summary}</p>
              </div>
            )}
          </div>
          <div className="flex justify-center items-center bg-slate-50 rounded-md border border-slate-200 p-2">
            <AssetQualityGauge score={asset_quality_score || 0} grade={asset_quality_grade || 'N/A'} />
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard 
            label="Max LTV" 
            value={`${loan_recommendations?.max_ltv_percent || 0}%`}
            subtext="Loan-to-Value"
            icon={DollarSign}
            color={(loan_recommendations?.max_ltv_percent || 0) >= 75 ? "emerald" : "amber"}
          />
          <MetricCard 
            label="Tenure" 
            value={`${loan_recommendations?.recommended_tenure_months || 0}M`}
            subtext="Recommended"
            icon={Clock}
            color="blue"
          />
          <MetricCard 
            label="Risk Premium" 
            value={`+${loan_recommendations?.risk_premium_bps || 0}bps`}
            subtext="Interest Adjustment"
            icon={TrendingDown}
            color={(loan_recommendations?.risk_premium_bps || 0) <= 50 ? "emerald" : "amber"}
          />
          <MetricCard 
            label="NPA Prob." 
            value={`${npa_risk_model?.final_npa_probability_percent || 0}%`}
            subtext="Default Risk"
            icon={AlertTriangle}
            color={getScoreColor(100 - (parseFloat(npa_risk_model?.final_npa_probability_percent) || 50))}
          />
        </div>

        {/* Depreciation Analysis with Chart */}
        <Section title="Depreciation Analysis" icon={TrendingDown} color="amber">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Depreciation Metrics */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-md">
                  <div className="text-sm text-slate-500 uppercase mb-1">Standard Rate</div>
                  <div className="text-xl font-bold text-slate-700">
                    {depreciation_analysis?.standard_rate_percent || 0}%
                  </div>
                </div>
                <div className="p-3 bg-amber-50 rounded-md">
                  <div className="text-sm text-amber-600 uppercase mb-1">Condition Adjusted</div>
                  <div className="text-xl font-bold text-amber-700">
                    {depreciation_analysis?.condition_adjusted_rate_percent || 0}%
                  </div>
                </div>
              </div>
              <div className="p-3 bg-blue-50 rounded-md">
                <div className="text-sm text-blue-600 uppercase mb-1">Brand Adjusted (×{depreciation_analysis?.brand_adjusted_rate_percent || 1})</div>
                <div className="text-xl font-bold text-blue-700">
                  Total: {depreciation_analysis?.total_adjusted_depreciation_percent || 0}%
                </div>
              </div>
              {depreciation_analysis?.justification && (
                <p className="text-sm text-slate-600 italic p-2 bg-slate-50 rounded-md">
                  {depreciation_analysis.justification}
                </p>
              )}
            </div>

            {/* Depreciation Curve Chart */}
            {depreciationChartData.length > 0 && (
              <div className="h-64">
                <div className="text-sm text-slate-500 uppercase mb-2 text-center">Depreciation Curve (5 Years)</div>
                <ResponsiveContainer width="100%" height="90%">
                  <LineChart data={depreciationChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="year" 
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickFormatter={(value) => `Y${value}`}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line 
                      type="monotone" 
                      dataKey="standard" 
                      stroke="#94a3b8" 
                      strokeWidth={2}
                      name="Standard"
                      dot={{ fill: '#94a3b8', r: 4 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="adjusted" 
                      stroke="#f59e0b" 
                      strokeWidth={2}
                      name="Condition Adjusted"
                      dot={{ fill: '#f59e0b', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Section>

        {/* NPA Risk Model with Chart */}
        <Section title="NPA Risk Assessment Model" icon={AlertTriangle} color="amber">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Risk Summary */}
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-slate-50 rounded-md">
                  <div className="text-xs text-slate-500 uppercase">Base NPA Rate</div>
                  <div className="text-lg font-bold text-slate-700">{npa_risk_model?.base_npa_rate_percent || 0}%</div>
                </div>
                <div className="p-2 bg-amber-50 rounded-md">
                  <div className="text-xs text-amber-600 uppercase">Adjustment</div>
                  <div className="text-lg font-bold text-amber-700">
                    {npa_risk_model?.condition_adjustment_percent > 0 ? '+' : ''}
                    {npa_risk_model?.condition_adjustment_percent || 0}%
                  </div>
                </div>
                <div className="p-2 bg-red-50 rounded-md">
                  <div className="text-xs text-red-600 uppercase">Final NPA</div>
                  <div className="text-lg font-bold text-red-700">{npa_risk_model?.final_npa_probability_percent || 0}%</div>
                </div>
              </div>
              
              {npa_risk_model?.npa_reduction_potential_percent && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-emerald-600" />
                    <span className="text-sm font-bold text-emerald-700 uppercase">NPA Reduction Potential</span>
                  </div>
                  <div className="text-xl font-bold text-emerald-700 mt-1">
                    -{npa_risk_model.npa_reduction_potential_percent}%
                  </div>
                  <p className="text-xs text-emerald-600">With this inspection report</p>
                </div>
              )}
            </div>

            {/* Risk Factors Chart */}
            {npaRiskFactors.length > 0 && (
              <div className="h-64">
                <div className="text-sm text-slate-500 uppercase mb-2 text-center">Risk Factor Breakdown</div>
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={npaRiskFactors} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#64748b' }} />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      tick={{ fontSize: 10, fill: '#64748b' }}
                      width={80}
                    />
                    <Tooltip 
                      formatter={(value, name) => [`${value}%`, name === 'contribution' ? 'Contribution' : 'Weight']}
                    />
                    <Bar dataKey="contribution" name="Risk Contribution" radius={[0, 4, 4, 0]}>
                      {npaRiskFactors.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Section>

        {/* Value Projections with Chart */}
        <Section title="Asset Value Projections" icon={BarChart3} color="blue">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Breakeven Analysis */}
            <div className="space-y-3">
              <div className="p-3 bg-blue-50 rounded-md">
                <div className="text-sm text-blue-600 uppercase mb-2">Breakeven Recovery Analysis</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-slate-500">Best Case</div>
                    <div className="text-xl font-bold text-emerald-600">
                      {value_projections?.breakeven_analysis?.recovery_months_best_case || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Worst Case</div>
                    <div className="text-xl font-bold text-amber-600">
                      {value_projections?.breakeven_analysis?.recovery_months_worst_case || 'N/A'}
                    </div>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-blue-200">
                  <div className="text-xs text-slate-500">Salvage Value</div>
                  <div className="text-lg font-bold text-slate-700">
                    {value_projections?.breakeven_analysis?.salvage_value_percent || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Market Position */}
              {market_position && (
                <div className="p-3 bg-slate-50 rounded-md">
                  <div className="text-sm text-slate-600 uppercase mb-2">Market Position</div>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-blue-100 text-blue-700">
                      Brand: {market_position.brand_strength}
                    </Badge>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      Demand: {market_position.segment_demand}
                    </Badge>
                    <Badge className="bg-purple-100 text-purple-700">
                      Liquidity: {market_position.resale_liquidity}
                    </Badge>
                  </div>
                </div>
              )}
            </div>

            {/* Value Projection Chart */}
            {valueProjectionData.length > 0 && (
              <div className="h-64">
                <div className="text-sm text-slate-500 uppercase mb-2 text-center">Value Retention Over Time</div>
                <ResponsiveContainer width="100%" height="90%">
                  <LineChart data={valueProjectionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="months" 
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickFormatter={(value) => `${value}M`}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickFormatter={(value) => `${value}%`}
                      domain={[0, 100]}
                    />
                    <Tooltip 
                      formatter={(value) => [`${value}%`, '']}
                      labelFormatter={(label) => `${label} Months`}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line 
                      type="monotone" 
                      dataKey="withMaintenance" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      name="With Maintenance"
                      dot={{ fill: '#10b981', r: 4 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="withoutMaintenance" 
                      stroke="#ef4444" 
                      strokeWidth={2}
                      name="Without Maintenance"
                      dot={{ fill: '#ef4444', r: 4 }}
                      strokeDasharray="5 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Section>

        {/* Loan Recommendations */}
        <Section title="Loan Structuring Recommendations" icon={DollarSign} color="blue">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="p-3 bg-blue-50 rounded-md text-center">
              <div className="text-sm text-blue-600 uppercase">Max LTV</div>
              <div className="text-2xl font-bold text-blue-800">{loan_recommendations?.max_ltv_percent}%</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-md text-center">
              <div className="text-sm text-blue-600 uppercase">Tenure</div>
              <div className="text-2xl font-bold text-blue-800">{loan_recommendations?.recommended_tenure_months}M</div>
            </div>
            <div className="p-3 bg-amber-50 rounded-md text-center">
              <div className="text-sm text-amber-600 uppercase">Risk Premium</div>
              <div className="text-2xl font-bold text-amber-700">+{loan_recommendations?.risk_premium_bps}bps</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-md text-center">
              <div className="text-sm text-slate-600 uppercase">EMI Buffer</div>
              <div className="text-2xl font-bold text-slate-700">{loan_recommendations?.emi_buffer_recommendation_percent}%</div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {loan_recommendations?.ltv_justification && (
              <div className="p-3 bg-slate-50 rounded-md">
                <div className="text-xs text-slate-500 uppercase mb-1">LTV Justification</div>
                <p className="text-sm text-slate-700">{loan_recommendations.ltv_justification}</p>
              </div>
            )}
            {loan_recommendations?.tenure_justification && (
              <div className="p-3 bg-slate-50 rounded-md">
                <div className="text-xs text-slate-500 uppercase mb-1">Tenure Justification</div>
                <p className="text-sm text-slate-700">{loan_recommendations.tenure_justification}</p>
              </div>
            )}
            {loan_recommendations?.premium_justification && (
              <div className="p-3 bg-slate-50 rounded-md">
                <div className="text-xs text-slate-500 uppercase mb-1">Premium Justification</div>
                <p className="text-sm text-slate-700">{loan_recommendations.premium_justification}</p>
              </div>
            )}
          </div>
        </Section>

        {/* Asset Monitoring Schedule */}
        <Section title="Asset Health Monitoring" icon={Target} color="emerald">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-emerald-50 rounded-md">
              <div className="text-sm text-emerald-600 uppercase">Inspection Frequency</div>
              <div className="text-xl font-bold text-emerald-700">
                Every {monitoring_schedule?.inspection_frequency_months || 6} months
              </div>
            </div>
          </div>
          
          {monitoring_schedule?.key_checkpoints && (
            <div className="mb-3">
              <div className="text-sm text-slate-500 uppercase mb-2">Key Checkpoints</div>
              <div className="flex flex-wrap gap-1">
                {monitoring_schedule.key_checkpoints.map((checkpoint, idx) => (
                  <Badge key={idx} className="bg-slate-100 text-slate-600 text-sm">{checkpoint}</Badge>
                ))}
              </div>
            </div>
          )}
          
          {monitoring_schedule?.alert_thresholds && (
            <div>
              <div className="text-sm text-slate-500 uppercase mb-2">Alert Thresholds</div>
              <div className="flex flex-wrap gap-1">
                {monitoring_schedule.alert_thresholds.map((threshold, idx) => (
                  <Badge key={idx} className="bg-amber-50 text-amber-600 text-sm">
                    <AlertTriangle size={10} className="mr-1" />{threshold}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Section>
      </CardContent>
    </Card>
  );
}
